import { currentSession } from "../state/session.js";

const OVERLAY_ID = "dropl-anti-devtools-overlay";
let installed = false;
let reattachTimer: number | undefined;
let detectTimer: number | undefined;
let warned = false;

const noop = (): void => undefined;
const CONSOLE_METHODS: (keyof Console)[] = [
  "log", "info", "warn", "error", "debug", "trace",
  "table", "dir", "dirxml", "group", "groupCollapsed", "groupEnd",
  "count", "countReset", "time", "timeEnd", "timeLog", "assert", "profile", "profileEnd",
];
const originalConsole: Partial<Record<keyof Console, unknown>> = {};

function canBypass(): boolean {
  const u = currentSession().user;
  return u?.capabilities.permissions.includes("client.devtools_access") ?? false;
}

function isDevToolsOpen(): boolean {
  const wDiff = window.outerWidth - window.innerWidth;
  const hDiff = window.outerHeight - window.innerHeight;
  return wDiff > 200 || hDiff > 200;
}

function blockContext(e: MouseEvent): void {
  e.preventDefault();
  e.stopImmediatePropagation();
}

function blockKeys(e: KeyboardEvent): void {
  if (e.key === "F12") { e.preventDefault(); e.stopImmediatePropagation(); return; }
  const k = e.key.toLowerCase();
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && (k === "i" || k === "j" || k === "c" || k === "k")) {
    e.preventDefault(); e.stopImmediatePropagation(); return;
  }
  if ((e.ctrlKey || e.metaKey) && k === "u") {
    e.preventDefault(); e.stopImmediatePropagation();
  }
}

function attachListeners(): void {
  document.addEventListener("contextmenu", blockContext, { capture: true });
  document.addEventListener("keydown",     blockKeys,    { capture: true });
  window.addEventListener  ("keydown",     blockKeys,    { capture: true });
}

function emitConsoleWarning(): void {
  if (warned) return;
  warned = true;
  const fn = originalConsole.log as ((...args: unknown[]) => void) | undefined;
  if (!fn) return;
  const stylesBanner = [
    "color:#fff",
    "background:#C1372A",
    "padding:18px 60px",
    "font:900 56px/1 system-ui,sans-serif",
    "border-radius:12px",
    "letter-spacing:6px",
    "text-shadow:0 2px 4px rgba(0,0,0,0.3)",
  ].join(";");
  const stylesSub = "font:700 16px system-ui,sans-serif;color:#C1372A;";
  const stylesBody = "font:14px/1.5 system-ui,sans-serif;color:#888;";
  fn.call(console, "%c STOP ", stylesBanner);
  fn.call(console, "%cZone réservée aux développeurs Dropl.", stylesSub);
  fn.call(console,
    "%cToute action ici peut compromettre votre compte. " +
    "Console verrouillée — les méthodes console.* sont neutralisées. " +
    "Pour un accès légitime, demandez la permission « client.devtools_access ».",
    stylesBody,
  );
}

function muteConsole(): void {
  for (const m of CONSOLE_METHODS) {
    if (originalConsole[m] === undefined) {
      originalConsole[m] = (console as unknown as Record<string, unknown>)[m];
    }
    (console as unknown as Record<string, unknown>)[m] = noop;
  }
}

function unmuteConsole(): void {
  for (const m of CONSOLE_METHODS) {
    const orig = originalConsole[m];
    if (orig !== undefined) {
      (console as unknown as Record<string, unknown>)[m] = orig;
    }
  }
}

function showOverlay(): void {
  if (document.getElementById(OVERLAY_ID)) return;
  const div = document.createElement("div");
  div.id = OVERLAY_ID;
  div.innerHTML = `
    <div class="adt__plate" aria-hidden="true">
      <i class="fa-solid fa-shield-halved"></i>
    </div>
    <p class="adt__title">Accès aux outils de développement non autorisé</p>
    <p class="adt__sub">Fermez la fenêtre DevTools pour continuer à utiliser Dropl.</p>
    <p class="adt__hint">Permission requise : <code>client.devtools_access</code></p>`;
  document.body.appendChild(div);
  document.documentElement.classList.add("adt-locked");
  document.body.classList.add("adt-locked");
  emitConsoleWarning();
}

function hideOverlay(): void {
  document.getElementById(OVERLAY_ID)?.remove();
  document.documentElement.classList.remove("adt-locked");
  document.body.classList.remove("adt-locked");
}

function startDetectionLoop(): void {
  if (detectTimer) return;
  detectTimer = window.setInterval(() => {
    if (isDevToolsOpen()) showOverlay();
    else hideOverlay();
    muteConsole();
  }, 750);
}

function stopAll(): void {
  document.removeEventListener("contextmenu", blockContext, { capture: true } as EventListenerOptions);
  document.removeEventListener("keydown",     blockKeys,    { capture: true } as EventListenerOptions);
  window.removeEventListener  ("keydown",     blockKeys,    { capture: true } as EventListenerOptions);
  if (reattachTimer) { window.clearInterval(reattachTimer); reattachTimer = undefined; }
  if (detectTimer)   { window.clearInterval(detectTimer);   detectTimer = undefined; }
  hideOverlay();
  unmuteConsole();
  installed = false;
}

function applyState(): void {
  if (canBypass()) {
    if (installed) stopAll();
    return;
  }
  if (installed) return;
  installed = true;
  muteConsole();
  emitConsoleWarning();
  attachListeners();
  reattachTimer = window.setInterval(attachListeners, 2000);
  startDetectionLoop();
}

export function initAntiDevtools(): void {
  applyState();
}
